import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";

import background from "@/assets/login/bg.webp";
import logo_alt from "@/assets/logotipo.svg";
import Button from "../../../shared/components/button";
import Input from "@/shared/components/input";
import { useAsync } from "@/shared/hooks/HookBase";
import { toast } from "@/shared/components/toast";
import AuthService from "../services/AuthService";

const authService = new AuthService();

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const token = searchParams.get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const { loading, error, execute } = useAsync();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }

    await execute(
      () => authService.resetPassword({ email, token, newPassword }),
      { successMessage: "Tu contraseña fue restablecida. Ya puedes iniciar sesión." },
    );
    navigate("/login");
  };

  return (
    <main className="grid grid-cols-1 lg:grid-cols-4 min-h-screen">
      <section
        className="hidden lg:block lg:col-span-3 relative"
        style={{
          backgroundImage: `url(${background})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <a href="/">
          <img
            className="mt-10 ml-10 lg:mt-20 lg:ml-20 h-10 lg:h-12 w-auto relative z-20"
            src={logo_alt}
            alt="Logo"
          />
        </a>
        <div className="absolute z-10 top-[85%] left-[75%] -translate-x-1/2 -translate-y-1/2 w-28 h-28 lg:w-32 lg:h-32 bg-black rounded-full shadow-2xl" />
      </section>
      <section className="lg:col-span-1 flex items-center justify-center px-6 sm:px-10 md:px-20 py-12 bg-black">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm lg:max-w-none gap-y-8 lg:gap-y-20 grid"
        >
          <div className="lg:hidden flex justify-center mb-8">
            <a href="/">
              <img className="h-10 w-auto" src={logo_alt} alt="Logo" />
            </a>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center lg:text-left text-white">
            Nueva contraseña
          </h1>

          <div className="grid gap-y-6">
            {!email || !token ? (
              <p className="text-red-500 text-xs">
                Este enlace no es válido. Solicita uno nuevo desde la pantalla de inicio de
                sesión.
              </p>
            ) : (
              <>
                <Input
                  type="password"
                  label="Nueva contraseña"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={12}
                />
                <Input
                  type="password"
                  label="Confirmar contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={12}
                />
              </>
            )}

            {error && <p className="text-red-500 text-xs mt-2">{error.message}</p>}
          </div>
          <Button
            variant="primary"
            submit
            className="w-full py-4 text-xs lg:text-sm tracking-widest font-bold"
            disabled={loading || !email || !token}
          >
            {loading ? "GUARDANDO..." : "GUARDAR CONTRASEÑA"}
          </Button>
        </form>
      </section>
    </main>
  );
}
